import type { TFunction } from 'i18next';

import {
    type FeedingPen,
    type KpiExclusionReason,
    kpiExclusionReason,
    type PenMetric,
} from '@/domain/feedingDashboard';

import { exclusionReasonText } from '../exclusionReasonText';
import type { SystemLabels } from '../systemLabel';

// The reasons a dash cannot explain by itself. An unmapped signal keeps the table's own,
// longer title, and a value that simply did not arrive stays untitled as before.
const TITLED: ReadonlySet<KpiExclusionReason> = new Set([
    'feedingServiceUnavailable',
    'noBioscope',
    'noBioscopeData',
    'collecting',
]);

/**
 * The title of a cell that shows no value, in the KPI row's words for the same pen (F6):
 * a failed feeding-service call, or a Bioscope that is missing or still collecting.
 */
export function cellReason(
    pen: FeedingPen,
    metric: PenMetric,
    shown: boolean,
    system: SystemLabels,
    t: TFunction
): string | undefined {
    if (shown) return undefined;
    const reason = kpiExclusionReason(pen, metric);
    if (!TITLED.has(reason)) return undefined;
    return exclusionReasonText(reason, t, { system: system.name, closingDay: null, period: false });
}

/**
 * IPS 34 counts a day without a plan as no plan, so Feed vs plan on a pen with plan gaps
 * says how many days carry a plan.
 */
export function planDaysTitle(pen: FeedingPen, t: TFunction): string | undefined {
    const coverage = pen.feedCoverage;
    if (pen.feedVsPlanPct == null || !coverage || coverage.planDays >= coverage.days) {
        return undefined;
    }
    return t('feedingModule.kpi.feedPlanDays', {
        covered: coverage.planDays,
        count: coverage.days,
    });
}
